import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { FunctionComponent, useCallback, useState } from "react";
import { Color } from "../colors";
import { stylesheet } from "../stylesheet";
import { UploadSection } from "./upload-section/UploadSection";
import { AttachmentLinkEnum, DocumentResponse } from "@akitabox/api-client";
import { useApiMutation } from "../hooks/useApiMutation";
import { api } from "../api";
import { FileListSection } from "./file-list-section/FileListSection";
import { fireAndforget } from "../../utils/fireAndForget";
import { useGlobalSnackbar } from "../consecutive-snackbar/GlobalSnackbar";
import { FilterKind } from "./file-list-section/FilterBar";

export interface AttachmentDialogProps extends DialogProps {
  organization: string;
  building: string;
  entityType: AttachmentLinkEnum;
  entityId: string;
}

export const AttachmentDialog: FunctionComponent<AttachmentDialogProps> = ({
  building,
  onClose,
  organization,
  entityType,
  entityId,
  ...rest
}) => {
  const { simple } = useGlobalSnackbar();

  /**
   * API call for uploading files to create abx documents
   */
  const { trigger: triggerDocumentUpload, isMutating: isUploadingDocuments } =
    useApiMutation(api.documents.upload);

  /**
   * API call for creating attachments
   */
  const { trigger: triggerAttachmentCreate, isMutating: isSavingAttachments } =
    useApiMutation(api.attachments.create);

  /**
   * Array of documents that have been created
   */
  const [createdDocuments, setDocuments] = useState<DocumentResponse[]>([]);
  /**
   * Array of files that have been selected but have not finished uploading.
   */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /**
   * Map of documents that have been selected from the documents list.
   */
  const [selectedFiles, setSelectedFiles] = useState<
    Record<DocumentResponse["_id"], DocumentResponse>
  >({});

  /**
   * Controls for the dialog's top progress bar. Each should be
   * in [0, 100). `buffer` is used to indicate the progress of
   * requests that are pending a response.
   *
   * @example
   * setProgressBar({ value: 50, buffer: 75 })
   * // would show something like
   * // ======---...
   */
  const [progressBar, setProgressBar] = useState({
    value: 0,
    buffer: 0,
  });

  /**
   * Event handler for the removal of an uploaded document pending
   * attachment.
   * @param document The document to "forget"
   */
  const handleDocumentRemove = (document: DocumentResponse) => {
    setDocuments((documents) => [
      ...documents.filter((doc) => doc._id !== document._id),
    ]);
  };

  /**
   * Event handler for files being submitted for uploading as abx documents.
   * @param file The file to upload
   * @returns A promise that resolves when
   */
  const handleFileUpload = async (file: File) => {
    // mark the file as pending upload
    setPendingFiles((files) => [...files, file]);
    try {
      const response = await triggerDocumentUpload({
        args: { building, file, attachment: true },
      });
      // track the newly created document
      setDocuments((documents) => [...documents, response.data[0]]);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Unknown network error");

      // display an error toast
      simple(error.message, { severity: "error" });
    } finally {
      // ensure we always clear the pending file
      setPendingFiles((files) => files.filter((f) => f !== file));
    }
  };

  /**
   * Event handler for closing the dialog.
   */
  const handleClose = useCallback<Exclude<typeof onClose, undefined>>(
    (event, reason) => {
      if (reason === "backdropClick") {
        return;
      }
      onClose?.(event, reason);
    },
    [onClose]
  );

  /**
   * Event handler for finalizing changes in the dialog. Creates all
   * attachments queued up in the dialog, and closes.
   */
  const handleSave = useCallback(() => {
    fireAndforget(async () => {
      const numAttachmentsToCreate =
        Object.keys(selectedFiles).length + createdDocuments.length;

      // percentage of progress completed with every document attached
      const progressTickSize = 100 / numAttachmentsToCreate;
      setProgressBar({
        buffer: 0,
        value: 0,
      });
      /**
       * Array of work to do to create attachments. Includes progress
       * bar manipulation, and error handling. None of these functions
       * will reject.
       */
      const attachFns = [
        ...createdDocuments,
        ...Object.values(selectedFiles),
      ].map((document) => async () => {
        setProgressBar((pb) => ({
          ...pb,
          buffer: pb.buffer + progressTickSize,
        }));
        try {
          await triggerAttachmentCreate({
            args: {
              building,
              attachment: {
                document: document._id,
                links: [
                  {
                    entity_id: entityId,
                    entity_type: entityType,
                  },
                ],
              },
            },
          });
        } catch (err: any) {
          simple(
            `Error attaching document ${document.name}${
              document.extension || ""
            }: ${err.message}`,
            { severity: "error" }
          );
        } finally {
          setProgressBar((pb) => ({
            ...pb,
            value: pb.value + progressTickSize,
          }));
        }
      });

      for (const attachDocument of attachFns) {
        // PERF: We could do several parallel calls here to cut down on time to
        // attach a large number of documents.
        await attachDocument();
      }

      simple(
        `Successfully attached ${numAttachmentsToCreate} file(s) to the ${entityType}`
      );

      // close the dialog
      handleClose({}, "escapeKeyDown");
    });
  }, [
    createdDocuments,
    selectedFiles,
    building,
    triggerAttachmentCreate,
    entityId,
    entityType,
    handleClose,
    simple,
  ]);

  const handleFileSelect = (file: DocumentResponse) => {
    setSelectedFiles((selectedFiles) => {
      if (selectedFiles[file._id]) {
        const { [file._id]: _, ...rest } = selectedFiles;
        return rest;
      }
      return {
        ...selectedFiles,
        [file._id]: file,
      };
    });
  };

  return (
    <>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        data-testid={AttachmentDialogTestids.Dialog}
        {...rest}
        onClose={handleClose}
      >
        <DialogTitle component={Box} css={ss.dialogTitle}>
          <Typography variant="h6" color="inherit">
            Attach Photos & Files
          </Typography>
          <IconButton
            data-testid={AttachmentDialogTestids.CloseButton}
            color="inherit"
            onClick={() => handleClose({}, "escapeKeyDown")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Progress bar shown during save. Hides itself at 0 progress automatically */}
        <LinearProgress
          data-testid={AttachmentDialogTestids.ProgressBar}
          variant="buffer"
          value={progressBar.value}
          valueBuffer={progressBar.buffer}
          css={
            progressBar.value === 0 && progressBar.buffer === 0
              ? ss.progressBarHidden
              : undefined
          }
        />
        <DialogContent>
          <Stack>
            <Typography css={ss.headerText} variant="body2" align="center">
              Upload and/or select photos & files to attach
            </Typography>
            <UploadSection
              css={ss.uploadSection}
              onDocumentRemove={handleDocumentRemove}
              onFileUpload={handleFileUpload}
              documents={createdDocuments}
              isLoading={isUploadingDocuments}
              pendingFiles={pendingFiles}
            />
            <FileListSection
              entityId={entityId}
              entityType={entityType}
              orgId={organization}
              building={building}
              selectedFiles={selectedFiles}
              onFileSelection={handleFileSelect}
            />
          </Stack>
        </DialogContent>{" "}
        <DialogActions>
          <Box css={{ width: "100%", fontStyle: "italic" }}>
            {Object.keys(selectedFiles).length > 0 ||
            Object.keys(createdDocuments).length > 0
              ? `${
                  Object.keys(selectedFiles).length +
                  Object.keys(createdDocuments).length
                } photo(s)/file(s) selected to be attached`
              : ""}
          </Box>
          <Button
            data-testid={AttachmentDialogTestids.CancelButton}
            onClick={() => handleClose({}, "escapeKeyDown")}
            css={{ fontWeight: "bold" }}
          >
            CANCEL
          </Button>
          <LoadingButton
            loading={isSavingAttachments}
            data-testid={AttachmentDialogTestids.SaveButton}
            variant="contained"
            color="primary"
            disabled={
              !(function isEnabled() {
                const hasPendingUploads = pendingFiles.length > 0;
                const hasUploadedDocuments = createdDocuments.length > 0;
                const hasSelectedDocuments =
                  Object.keys(selectedFiles).length > 0;
                if (
                  hasPendingUploads ||
                  isUploadingDocuments ||
                  isSavingAttachments
                ) {
                  return false;
                }
                return hasUploadedDocuments || hasSelectedDocuments;
              })()
            }
            onClick={handleSave}
            disableElevation
          >
            SAVE
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AttachmentDialogTestids = {
  /** Test id for the root of the dialog */
  Dialog: "attachmentDialog",
  /** Top-level dialog progress bar */
  ProgressBar: "attachmentDialog.ProgressBar",
  /** Test id for the SAVE button */
  SaveButton: "attachmentDialog.saveButton",
  /** Test id for the CANCEL button */
  CancelButton: "attachmentDialog.cancelButton",
  /** Test id for the x button in the dialog title */
  CloseButton: "attachmentDialog.closeButton",
  /**
   * Test id for the upload button button. Element will contain
   * the actual file input.
   */
  UploadButton: "attachmentDialog.uploadButton",
  /**
   * Test id for the row containing a file.
   * @example
   * `attachmentDialog.uploadListItem.sample.pdf`
   */
  FileListItem: (fileName: string) =>
    `attachmentDialog.uploadListItem.${fileName}`,
  /** Test id for the empty list item in file list section */
  FileListEmpty: "fileListEmpty",
  /** Test id for the toast error component in file list section */
  FileListError: "fileListError",
  /** Test id for the loading indicator in file list section */
  LoadingIndicator: "loadingIndicator",
  /** Test id for the file name text in file list section */
  AttachmentFileName: "attachmentFileName",
  /** Test id for the file size text in file list section */
  AttachmentFileSize: "attachmentFileSize",
  /** Test id for the file thumbnail placeholder in file list section */
  AttachmentPlaceholder: "attachmentPlaceholder",
  /**
   * Test id for rows in the document list
   * @example
   * `attachmentDialog.documentList.row.sample.pdf`
   */
  DocumentRow: (fileName: string) =>
    `attachmentDialog.documentList.row.${fileName}`,
  /**
   * Test id for checkboxes in the document list
   */
  DocumentCheckbox: `attachmentDialog.documentList.row.checkbox`,
  /**
   * Test id for the document thumbnail in each row of the document
   * list
   */
  DocumentThumbnail: `attachmentDialog.documentList.row.thumbnail`,
  /** Test id for filter bar's Filter button */
  FilterButton: "attachmentDialog.filterButton",
  /** Test id for filter bar's filter type dropdown */
  FilterSelect: "attachmentDialog.filterSelect",
  /** Test id for filter bar's filter value input */
  FilterInput: "attachmentDialog.filterInput",
  /**
   * Test id for filter bar chips
   * @example
   * `attachmentDialog.filterChip.name`
   */
  FilterChip: (filterKind: FilterKind) =>
    `attachmentDialog.filterChip.${filterKind}`,
  FilterClearAllButton: `attachmentDialog.filterClearAllbutton`,
};

const ss = stylesheet({
  dialogTitle: {
    backgroundColor: Color.MidnightExpress,
    color: Color.White,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerText: (theme) => ({
    marginTop: theme.spacing(2),
  }),
  uploadSection: (theme) => ({
    flexGrow: 1,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }),
  progressBarHidden: {
    visibility: "hidden",
  },
});
